import React, { useState, useCallback } from 'react';
import { Copy, Check, Loader2, Image as ImageIcon } from 'lucide-react';
import { authFetch } from '../lib/authFetch';

// Branded SVG icons
const EmailIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
  </svg>
);

const WhatsAppIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="#25D366">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const LinkedInIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="#0A66C2">
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
  </svg>
);

const XIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
);

const ShareAnalysisIcons = ({ analysisText, entityName, shareUrl, theme, label = 'Sherpa Analysis', dealMeta, counterparties = [], executives = [], dealId }) => {
  const [copied, setCopied] = useState(false);
  const [imgState, setImgState] = useState('idle'); // idle | loading | done
  const [aiLoading, setAiLoading] = useState(false);
  const isDark = theme === 'dark';

  // Put the deal's share card on the clipboard as a PNG, for pasting straight
  // into an email, a deck or a chat.
  //
  // Sharing the LINK already unfurls into this same image everywhere that
  // reads OG tags — this is for the places that do not, and for when you want
  // the picture rather than a preview of it.
  //
  // Safari will not accept a Blob resolved after an await inside the click
  // handler; it wants the ClipboardItem constructed with the pending promise
  // so the write stays inside the user gesture. Chrome accepts both. Browsers
  // that support neither open the card in a tab, where "save image" works.
  const handleCopyImage = useCallback(async () => {
    if (!dealId) return;
    const url = `/api/deal-card?deal=${encodeURIComponent(dealId)}`;
    setImgState('loading');
    try {
      const fetchPng = fetch(url).then((r) => {
        if (!r.ok) throw new Error(`card ${r.status}`);
        return r.blob();
      });
      if (typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
        await navigator.clipboard.write([new ClipboardItem({ 'image/png': fetchPng })]);
      } else {
        await fetchPng;
        window.open(url, '_blank', 'noopener');
      }
      setImgState('done');
      setTimeout(() => setImgState('idle'), 2000);
    } catch {
      // Never leave the button spinning: fall back to showing the image.
      window.open(url, '_blank', 'noopener');
      setImgState('idle');
    }
  }, [dealId]);

  // Fetch AI-generated social copy for a specific platform
  const fetchAiCopy = useCallback(async (platform) => {
    if (!dealId && !entityName) return null;
    try {
      setAiLoading(true);
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 4000); // 4s max wait
      const resp = await authFetch('/api/social-copy', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          dealId,
          headline: entityName,
          description: analysisText?.substring(0, 500),
          counterparts: counterparties,
          platform,
        }),
        signal: controller.signal,
      });
      clearTimeout(timer);
      if (!resp.ok) return null;
      const data = await resp.json();
      return data.text || null;
    } catch {
      return null; // Timeout or error — fall back to static
    } finally {
      setAiLoading(false);
    }
  }, [dealId, entityName, analysisText, counterparties]);

  const truncate = (text, max) => {
    if (!text || text.length <= max) return text;
    const cut = text.lastIndexOf(' ', max);
    return text.substring(0, cut > 0 ? cut : max) + '...';
  };

  // Build a concise social-ready summary (2-3 sentences from the first paragraph only)
  const buildSocialText = (fullText, maxChars) => {
    if (!fullText) return '';
    // Take first paragraph only
    const firstPara = fullText.split(/\n\n+/)[0] || fullText;
    // Extract first 2-3 sentences
    const sentences = firstPara.match(/[^.!?]+[.!?]+/g) || [firstPara];
    let result = '';
    for (const s of sentences.slice(0, 3)) {
      if ((result + s.trim()).length > maxChars) break;
      result += (result ? ' ' : '') + s.trim();
    }
    return result || truncate(firstPara, maxChars);
  };

  // Build executive line: "Key executives: John Smith (CEO), Jane Doe (CFO)"
  const execLine = executives.length > 0
    ? `\nKey executives: ${executives.slice(0, 4).map(e => `${e.name}${e.title ? ` (${e.title})` : ''}`).join(', ')}`
    : '';

  // Generate hashtags from deal metadata + counterparty names
  const buildHashtags = () => {
    const tags = [];
    if (dealMeta) {
      // Technology hashtags
      const techs = Array.isArray(dealMeta.technology) ? dealMeta.technology : (dealMeta.technology ? [dealMeta.technology] : []);
      const techMap = {
        solar: 'Solar', solar_pv: 'SolarPV', onshore_wind: 'OnshoreWind', offshore_wind: 'OffshoreWind',
        wind: 'WindEnergy', storage: 'EnergyStorage', hydrogen: 'Hydrogen', green_ammonia: 'GreenAmmonia',
        data_center: 'DataCenters', nuclear: 'Nuclear', biomass: 'Biomass', geothermal: 'Geothermal',
        gas: 'NaturalGas', oil_gas: 'OilAndGas', hydro: 'Hydropower', transmission: 'Transmission',
        hybrid: 'HybridEnergy', hospitality: 'Hospitality', transport: 'Transport', industrial: 'Industrial',
        agriculture: 'Agriculture', mining: 'Mining', defense: 'Defense', telecom: 'Telecom',
        water_waste: 'WaterInfrastructure', ev_charging: 'EVCharging',
      };
      techs.forEach(t => { if (techMap[t]) tags.push(techMap[t]); });
      // Country hashtag
      const countries = Array.isArray(dealMeta.country) ? dealMeta.country : (dealMeta.country ? [dealMeta.country] : []);
      countries.forEach(c => { if (c) tags.push(c.replace(/\s+/g, '')); });
      // Deal type hashtag
      const typeMap = {
        acquisition: 'Acquisition', ppa: 'PPA', financing: 'ProjectFinance', development: 'Development',
        divestiture: 'Divestiture', joint_venture: 'JointVenture', merger: 'MergersAndAcquisitions',
        concession: 'Concession', ipo: 'IPO', refinancing: 'Refinancing',
      };
      if (dealMeta.dealType && typeMap[dealMeta.dealType]) tags.push(typeMap[dealMeta.dealType]);
    }
    // Counterparty name hashtags — strip suffixes, spaces, special chars
    counterparties.forEach(name => {
      const clean = name
        .replace(/\b(Inc\.?|Ltd\.?|LLC|PLC|plc|Corp\.?|Corporation|Company|Group|S\.?A\.?|AG|GmbH|Pty|Pvt|Limited|Holdings?|Partners?|Capital|Management|Energy|Renewables?|International)\b/gi, '')
        .trim()
        .replace(/[^a-zA-Z0-9]/g, '');
      if (clean.length >= 2) tags.push(clean);
    });
    // Always add infrastructure
    tags.push('Infrastructure');
    // Dedupe
    const seen = new Set();
    return tags.filter(t => { const key = t.toLowerCase(); if (seen.has(key)) return false; seen.add(key); return true; }).map(t => `#${t}`).join(' ');
  };

  const hashtags = buildHashtags();
  const socialText = buildSocialText(analysisText, 500);

  const handleCopy = async () => {
    const aiText = await fetchAiCopy('generic');
    const body = aiText || `${socialText}${execLine}`;
    await navigator.clipboard.writeText(`${entityName}\n\n${body}\n\n${shareUrl}\n\n${hashtags}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleEmail = () => {
    const subject = encodeURIComponent(`${entityName}`);
    const body = encodeURIComponent(`${socialText}${execLine}\n\n${shareUrl}\n\n${hashtags}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleWhatsApp = async () => {
    const aiText = await fetchAiCopy('whatsapp');
    const body = aiText || truncate(socialText, 400) + execLine;
    const text = `*${entityName}*\n\n${body}\n\n${shareUrl}\n\n${hashtags}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
  };

  // LinkedIn-specific hashtag set: 3-5 sector/geo/category tags only.
  // Drops counterparty names entirely (low traffic + typo-prone — e.g.
  // "CooperatieveRabobank" had a typo in production posts) and the generic
  // #Infrastructure tag. LinkedIn's algorithm down-ranks posts with 8+ tags;
  // sector/geo tags reach the intended infrastructure-finance audience.
  const buildLinkedInHashtags = () => {
    const tags = [];
    if (dealMeta) {
      const techs = Array.isArray(dealMeta.technology) ? dealMeta.technology : (dealMeta.technology ? [dealMeta.technology] : []);
      const techMap = {
        solar: 'Solar', solar_pv: 'SolarPV', onshore_wind: 'OnshoreWind', offshore_wind: 'OffshoreWind',
        wind: 'WindEnergy', storage: 'EnergyStorage', hydrogen: 'Hydrogen', green_ammonia: 'GreenAmmonia',
        data_center: 'DataCenters', nuclear: 'Nuclear', biomass: 'Biomass', geothermal: 'Geothermal',
        gas: 'NaturalGas', oil_gas: 'OilAndGas', hydro: 'Hydropower', transmission: 'Transmission',
        hybrid: 'HybridEnergy', hospitality: 'Hospitality', transport: 'Transport', industrial: 'Industrial',
        agriculture: 'Agriculture', mining: 'Mining', defense: 'Defense', telecom: 'Telecom',
        water_waste: 'WaterInfrastructure', ev_charging: 'EVCharging',
      };
      techs.forEach(t => { if (techMap[t]) tags.push(techMap[t]); });
      const countries = Array.isArray(dealMeta.country) ? dealMeta.country : (dealMeta.country ? [dealMeta.country] : []);
      countries.forEach(c => { if (c) tags.push(c.replace(/\s+/g, '')); });
      const typeMap = {
        acquisition: 'MergersAndAcquisitions', ppa: 'PPA', financing: 'ProjectFinance',
        development: 'Development', divestiture: 'Divestiture', joint_venture: 'JointVenture',
        merger: 'MergersAndAcquisitions', concession: 'Concession', ipo: 'IPO', refinancing: 'Refinancing',
        supply_agreement: 'SupplyAgreement', investment: 'Investment', partnership: 'Partnership',
      };
      if (dealMeta.dealType && typeMap[dealMeta.dealType]) tags.push(typeMap[dealMeta.dealType]);
    }
    // Dedupe + cap at 5
    const seen = new Set();
    return tags
      .filter(t => { const k = t.toLowerCase(); if (seen.has(k)) return false; seen.add(k); return true; })
      .slice(0, 5)
      .map(t => `#${t}`)
      .join(' ');
  };

  const handleLinkedIn = async () => {
    // Build the LinkedIn-optimised post:
    //   Hook (numbers stacked) — paragraph 1
    //   Body (who/what/where + named parties) — paragraph 2
    //   POV (editorial one-liner) — paragraph 3
    //   CTA line + share URL
    //   5 hashtags max (sector/geo/category only)
    // The AI returns the three structured paragraphs from api/social-copy.js.
    const aiText = await fetchAiCopy('linkedin');
    const body = aiText || `${entityName}\n\n${truncate(socialText, 400)}${execLine}`;
    const cta = `Full deal breakdown — parties, technology, capacity — on Global Infrastructure Sherpa:\n${shareUrl}`;
    const tags = buildLinkedInHashtags();
    const postText = `${body}\n\n${cta}${tags ? `\n\n${tags}` : ''}`;
    await navigator.clipboard.writeText(postText);
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, '_blank');
  };

  const handleTwitter = async () => {
    const aiText = await fetchAiCopy('twitter');
    const tagStr = hashtags ? ' ' + hashtags.split(' ').slice(0, 3).join(' ') : '';
    const text = aiText
      ? truncate(aiText, 240 - tagStr.length - shareUrl.length - 2) + tagStr
      : truncate(`${entityName}`, 240 - tagStr.length - shareUrl.length - 2) + tagStr;
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
  };

  const iconBtn = `p-1.5 rounded-lg transition-colors ${
    isDark
      ? 'hover:bg-slate-600/50 text-slate-400 hover:text-slate-200'
      : 'hover:bg-slate-200/70 text-slate-500 hover:text-slate-700'
  }`;

  return (
    <div className="flex items-center gap-0.5">
      {aiLoading && <Loader2 size={14} className="animate-spin text-blue-500 mr-1" />}
      <button onClick={handleCopy} className={iconBtn} title="Copy analysis" disabled={aiLoading}>
        {copied ? <Check size={14} className="text-green-500" /> : <Copy size={14} />}
      </button>
      {dealId && (
        <button onClick={handleCopyImage} className={iconBtn} title="Copy card as image" disabled={imgState === 'loading'}>
          {imgState === 'loading' ? <Loader2 size={14} className="animate-spin" />
            : imgState === 'done' ? <Check size={14} className="text-green-500" />
              : <ImageIcon size={14} />}
        </button>
      )}
      <button onClick={handleEmail} className={iconBtn} title="Share via email">
        <EmailIcon size={14} />
      </button>
      <button onClick={handleWhatsApp} className={iconBtn} title="Share on WhatsApp">
        <WhatsAppIcon size={14} />
      </button>
      <button onClick={handleLinkedIn} className={iconBtn} title="Share on LinkedIn">
        <LinkedInIcon size={14} />
      </button>
      <button onClick={handleTwitter} className={iconBtn} title="Share on X">
        <XIcon size={14} />
      </button>
    </div>
  );
};

export default ShareAnalysisIcons;
